import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { HideOverflow } from '@ff-os/shared-react/components/hide-overflow/HideOverflow';
import { WindowBody } from '@ff-os/shared-react/components/window/partials/WindowBody';
import { WindowHeader } from '@ff-os/shared-react/components/window/partials/WindowHeader';
import { Window } from '@ff-os/shared-react/components/window/Window';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { selectAccessorySizes } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { selectPhotoIdsByGroupId, actions as photoActions } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { selectActiveVariant } from '../../_redux-groups';
import { selectQuantityById } from '../../_redux-photos';
import { setFrameId, setAccessoryId } from "../../_redux-ui";
import { applyAccessoryThunk } from '../../thunks/applyAccessoryThunk';
import { AccessoryPrintFrame } from './AccessoryPrintFrame';
import styles from './MultipleAccessoriesWindow.module.scss';

const _MultipleAccessoriesWindow = (({
    isOpen, close, activeAccessory, clearMobileAssetIds,
    photoIds, applyAccessory, quantities, setQuantity, activeVariant
}: Props) => {

    const { t } = useTranslation();

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const accessories = useSelector((state: PhotoPrintsPdpState) => selectAccessorySizes(state));

    const accessory = accessories.find(acc => acc.id === activeAccessory);
    const minimumAccessoryQuantity = accessory?.minimumQuantity || 1;

    const totalSelectedQuantity = useMemo(() => {
        return selectedIds.reduce((sum, id) => {
            const qty = quantities[id] ?? 0;
            return sum + qty;
        }, 0);
    }, [selectedIds, quantities]);

    const nextRequiredQuantity
        = (Math.ceil(totalSelectedQuantity / minimumAccessoryQuantity)
            * minimumAccessoryQuantity)
                || minimumAccessoryQuantity;

    if (!isOpen) {
        return null;
    }

    const accessoryTitle = t(accessory!.name);

    const onClose = () => {
        clearMobileAssetIds();
        close();
    };

    const handleApply = () => {
        applyAccessory(activeAccessory, selectedIds, activeVariant.borderId);
        setSelectedIds([]);
        clearMobileAssetIds();
        onClose();
    };

    return (
        <WindowStackItem>
            <Window close={onClose} size='xl' mobileOrigin='center'>
                <WindowHeader onClose={onClose} className={styles.header}>
                    <span>
                        {`${t('image_uploader.select_to_use', { accessory: accessoryTitle })}: `}
                    </span>
                </WindowHeader>

                <WindowBody>
                    <HideOverflow>
                        <Cell className={styles.photosCell}>
                            <div className={styles.photosOverflowCell}>
                                {photoIds.map(id => (
                                    <AccessoryPrintFrame
                                        key={id}
                                        id={id}
                                        selectedIds={selectedIds}
                                        setSelectedIds={setSelectedIds} />
                                ))}
                            </div>
                        </Cell>
                        <Cell className={styles.buttonCell}>
                            <Button
                                className={styles.cancelButton}
                                style="outline"
                                onClick={onClose} >
                                {t('commons.cancel')}
                            </Button>
                            <p className={styles.quantityText}>
                                <span>
                                    {t('image_uploader.selected_of_number', {
                                        selected: totalSelectedQuantity,
                                        required: nextRequiredQuantity
                                    })}
                                    {` ${t('image_uploader.selected_photos')}`}
                                </span>
                            </p>
                            <Button
                                className={styles.applyButton}
                                style="primary"
                                onClick={handleApply}>
                                {t('commons.next')}
                            </Button>
                        </Cell>
                    </HideOverflow>
                </WindowBody>
            </Window>
        </WindowStackItem>
    );
});

type Props = {
    isOpen: boolean;
    close: () => void;
    onUpload?: () => void;
    applyAccessory: (accessoryId: string, selectedPhotos: string[], borderId?: string) => void;
    setQuantity: (quantity: number, photoId: string) => void;
    clearMobileAssetIds: () => void;
    groupId: string;
    photoIds: string[];
    quantities: Record<string, number | undefined>;
    activeAccessory: string;
    activeVariant: PhotoPrintsJson.Variant;
};

type OwnProps = {
    groupId: string;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => {

    const photoIds = selectPhotoIdsByGroupId(state, ownProps.groupId);
    const quantities = Object.fromEntries(
        photoIds.map(id => [id, selectQuantityById(state, id)])
    );
    const activeVariant = selectActiveVariant(state, ownProps.groupId);

    return {
        photoIds,
        quantities,
        activeVariant
    };
};

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    applyAccessory: (accessoryId: string, selectedPhotos: string[], borderId?: string) => {
        dispatch(applyAccessoryThunk({
            groupId: ownProps.groupId,
            accessoryId: accessoryId,
            selectedPhotos: selectedPhotos,
            borderId: borderId
        }));
    },
    setQuantity: (quantity: number, photoId: string) => dispatch(photoActions.setQuantity({
        photoId: photoId,
        quantity: quantity
    })),
    clearMobileAssetIds: () => {
        dispatch(setFrameId(""));
        dispatch(setAccessoryId(""));
    }
});

export const MultipleAccessoriesWindow = connect(
    mapStateToProps, mapDispatchToProps
)(_MultipleAccessoriesWindow);